import { db } from "../firebaseClient"
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  collection,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore"
import { logPlatformEvent } from "./eventHelpers"

// Creates a new course in draft status
export const createCourse = async ({ orgId, title, courseType, thumbnailUrl, createdByUid }) => {
  if (!orgId || !title?.trim()) {
    return { courseId: null, error: { message: "Org ID and title are required" } }
  }

  try {
    const courseRef = doc(collection(db, "organizations", orgId, "courses"))
    await setDoc(courseRef, {
      title: title.trim(),
      titleLower: title.trim().toLowerCase(),
      description: "",
      thumbnailUrl: thumbnailUrl?.trim() || "",
      category: "Other",
      visibility: "private",
      courseType: courseType || "course",
      status: "draft",
      enrollmentType: "invite",
      certification: {
        enabled: false,
        passingScore: 80,
        requiresAssessment: false,
      },
      pricing: {
        isFree: true,
        amount: 0,
        currency: "USD",
      },
      modules: [],
      stats: {
        totalLearners: 0,
        avgCompletion: 0,
      },
            createdBy: createdByUid || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })

    logPlatformEvent({
      type: "COURSE_CREATED",
      actorUid: createdByUid || null,
      orgId,
      targetPath: `organizations/${orgId}/courses/${courseRef.id}`,
      metadata: { title: title.trim(), courseType: courseType || "course" },
    })

    return { courseId: courseRef.id, error: null }
  } catch (error) {
    return { courseId: null, error }
  }
}

// Lists all courses for an org, most recently updated first
export const listCourses = async (orgId) => {
  try {
    const coursesRef = collection(db, "organizations", orgId, "courses")
    const q = query(coursesRef, orderBy("updatedAt", "desc"))
    const snap = await getDocs(q)
    const courses = snap.docs?.map((d) => ({ courseId: d.id, ...d.data() }))
    return { courses, error: null }
  } catch (error) {
    // orderBy requires an index once you have enough docs with mixed missing fields;
    // fall back to unordered fetch so the panel doesn't break before the index exists
    try {
      const coursesRef = collection(db, "organizations", orgId, "courses")
      const snap = await getDocs(coursesRef)
      const courses = snap.docs?.map((d) => ({ courseId: d.id, ...d.data() }))
      return { courses, error: null }
    } catch (fallbackError) {
      return { courses: [], error: fallbackError }
    }
  }
}

// Fetches a single course
export const getCourse = async (orgId, courseId) => {
  try {
    const courseRef = doc(db, "organizations", orgId, "courses", courseId)
    const snap = await getDoc(courseRef)
    if (!snap.exists()) return { course: null, error: null }
    return { course: { courseId: snap.id, ...snap.data() }, error: null }
  } catch (error) {
    return { course: null, error }
  }
}

// Updates top-level course fields (title, description, settings, status, etc.)
export const updateCourse = async (orgId, courseId, updates) => {
  try {
    const courseRef = doc(db, "organizations", orgId, "courses", courseId)
    const finalUpdates = { ...updates, updatedAt: serverTimestamp() }

    // Keep titleLower in sync whenever title changes, so catalog sorting stays correct
    if (updates?.title !== undefined) {
      finalUpdates.titleLower = updates.title?.trim()?.toLowerCase() || ""
    }

    await updateDoc(courseRef, finalUpdates)
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// Deletes a course entirely
export const deleteCourse = async (orgId, courseId) => {
  try {
    const courseRef = doc(db, "organizations", orgId, "courses", courseId)
    await deleteDoc(courseRef)
    logPlatformEvent({
      type: "COURSE_DELETED",
      orgId,
      targetPath: `organizations/${orgId}/courses/${courseId}`,
      severity: "warning",
      metadata: {},
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// Overwrites the full modules array for a course. The course builder UI edits modules/lessons
// as a whole tree client-side, then saves the entire structure in one write — simpler and
// cheaper than granular per-module/per-lesson Firestore operations on a nested array.
export const updateCourseModules = async (orgId, courseId, modules) => {
  try {
    const courseRef = doc(db, "organizations", orgId, "courses", courseId)
    await updateDoc(courseRef, {
      modules,
      updatedAt: serverTimestamp(),
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// Helper to generate a short unique id for a new module or lesson (client-side, no Firestore round trip)
export const generateLocalId = () => {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`
}

// Publishes a course — flips status once modules/lessons are ready.
// Kept separate from updateCourse so the dashboard "Publish" button stays a single clear action.
export const publishCourse = async (orgId, courseId) => {
  const result = await updateCourse(orgId, courseId, { status: "published" })
  if (!result?.error) {
    logPlatformEvent({
      type: "COURSE_PUBLISHED",
      orgId,
      targetPath: `organizations/${orgId}/courses/${courseId}`,
      metadata: {},
    })
  }
  return result
}

export const unpublishCourse = async (orgId, courseId) => {
  return updateCourse(orgId, courseId, { status: "draft" })
}
